import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  AddressShipmentDTO,
  ShipmentDTO,
  SearchShipmentDTO,
  SearchMetadataDto,
  ShipmentPackageDTO,
  CreateOrUpdateShipmentDTO,
  DocumentDTO,
  MessageResult,
  CourierServiceCodesConfigurationDto,
  ConfigurationDTO,
  ManagePickupRequestDTO,
  Sv4bConfigurationDTO,
  ApiResponse,
  ShipImportNotificationDto,
  EcommerceOutcomeDto,
  DepartmentDTO,
  ProformaDTO,
  ProformaDetailsDTO,
  PaperlessDocumentDto,
  InvoiceCheckConfigurationDto,
  ShipmentRatesResultDTO,
  ResultLockUnlockDto
} from "@/model/data.model";
import {
  DistanceResultDef,
  GetShipmentResults,
  SearchParams,
  ShipmentSearchFilters,
  ShipmentListItem,
  Pageable,
  Page,
  ColumnDTO,
  WineWarehouseImporterDTO
} from "@/model/component.model";
import {
  getMultiStoreId,
  getTenantId,
  getStoreId,
  retrieveToken,
  retrieveKeycloakToken
} from "./AuthService";
import store from "../vuex/store";
import * as _ from "lodash";
import { addMonths, endOfYear, format, getYear, startOfYear } from "date-fns";
import { AxiosRequestConfigDef } from "@/event-bus";

export class ShipmentService {
  static getShipments(
    customerId: number,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 20,
      orderField: "",
      orderDirection: ""
    } as SearchParams,
    filters: ShipmentSearchFilters,
    tabSelected?: string
  ): Promise<GetShipmentResults> {
    const filtersParam = this.filtersParams(
      customerId,
      search,
      filters,
      tabSelected
    );
    const shipmentSearchFilters = [
      ...filtersParam[0],
      {
        field: "shipMBEProducts.idSpecialtyBusiness",
        type: "INTEGER",
        operation: "!=",
        values: [6, 8, 9, 10]
      }
    ];
    const orFilters = filtersParam[1];
    const sortBy = filtersParam[2];
    const config: AxiosRequestConfig = {
      method: "POST",
      url:
        store.getters.BASE_API_URL +
        "/shipments/search/shipments-management-mol",
      data: {
        and: shipmentSearchFilters,
        or: orFilters.length > 0 ? orFilters : undefined
      },
      params: {
        page: search.page,
        pageSize: search.pageSize,
        sortBy: sortBy
      }
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const shipments = res.data.content as SearchShipmentDTO[];
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.number,
        pageSize: res.data.size
      } as SearchParams;

      return { shipments, searchParams } as GetShipmentResults;
    });
  }

  static getWineShipments(
    customerId: number,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 20,
      orderField: "",
      orderDirection: ""
    } as SearchParams,
    filters: ShipmentSearchFilters,
    tabSelected?: string
  ): Promise<GetShipmentResults> {
    const filtersParam = this.filtersParams(
      customerId,
      search,
      filters,
      tabSelected
    );

    let shipmentSearchFilters = filtersParam[0];
    shipmentSearchFilters.push({
      field: "shipMBEProducts.idSpecialtyBusiness",
      type: "INTEGER",
      operation: "=",
      values: [6, 8, 9, 10]
    });
    shipmentSearchFilters = shipmentSearchFilters.filter((el: any) => {
      return el.field !== "createdByUser";
    });

    const orFilters = filtersParam[1];
    const sortBy = filtersParam[2];
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/search/shipments-wine2-mol",
      data: {
        and: shipmentSearchFilters,
        or: orFilters.length > 0 ? orFilters : undefined
      },
      params: {
        page: search.page,
        pageSize: search.pageSize,
        sortBy: sortBy
      }
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const shipments = res.data.content as SearchShipmentDTO[];
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.number,
        pageSize: res.data.size
      } as SearchParams;

      return { shipments, searchParams } as GetShipmentResults;
    });
  }

  static filtersParams(
    customerId: number,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 20,
      orderField: "",
      orderDirection: ""
    } as SearchParams,
    filters: ShipmentSearchFilters,
    tabSelected?: string
  ) {
    let shipmentSearchFilters: any = [
      {
        field: "customerId",
        type: "INTEGER",
        operation: "=",
        values: [customerId]
      },
      {
        field: "active",
        type: "BOOLEAN",
        operation: "=",
        values: ["true"]
      }
    ];

    const orFilters = [];

    let hasTracking = false;

    if (store.getters["customer/config"].onlySelectedDepartments) {
      const departments: DepartmentDTO[] =
        store.getters["customer"].departments;
      shipmentSearchFilters.push({
        field: "customerDepartmentId",
        type: "INTEGER",
        operation: "=",
        values: departments.filter(d => d.departmentId).map(d => d.departmentId)
      });
    }

    if (!store.getters["customer/config"].showShipmentsWithSameCustomerId) {
      shipmentSearchFilters.push({
        field: "createdByUser",
        type: "TEXT",
        operation: "=",
        values: [store.getters["customer/config"].username]
      });
    }
    if (filters.dateStart !== null) {
      const newDate =
        filters.dateStart.getTime() -
        filters.dateStart.getTimezoneOffset() * 60000;
      filters.dateStart = new Date(newDate);
      shipmentSearchFilters.push({
        field: "shipmentDate",
        type: "DATE",
        operation: ">=",
        values: [filters.dateStart.toISOString()]
      });
    }
    if (filters.dateEnd != null) {
      const newDate =
        filters.dateEnd.getTime() - filters.dateEnd.getTimezoneOffset() * 60000;
      filters.dateEnd = new Date(newDate);
      shipmentSearchFilters.push({
        field: "shipmentDate",
        type: "DATE",
        operation: "<=",
        values: [filters.dateEnd.toISOString()]
      });
    }
    if (filters.mbeService != null) {
      shipmentSearchFilters.push({
        field: "mbeServiceId",
        type: "INTEGER",
        operation: "=",
        values: [filters.mbeService.idMBEBusiness]
      });
    }
    if (filters.courier != null) {
      shipmentSearchFilters.push({
        field: "courierId",
        type: "INTEGER",
        operation: "=",
        values: [filters.courier.idMBEBusiness]
      });
    }
    if (filters.courierService != null) {
      shipmentSearchFilters.push({
        field: "courierServiceId",
        type: "INTEGER",
        operation: "=",
        values: [filters.courierService.idBusiness]
      });
    }
    if (filters.shipmentStatus != null) {
      shipmentSearchFilters.push({
        field: "status",
        type: "TEXT",
        operation: "=",
        values: filters.shipmentStatus
      });
    }
    if (filters.notStatuses != null) {
      shipmentSearchFilters.push({
        field: "status",
        type: "TEXT",
        operation: "!=",
        values: filters.notStatuses
      });
    }
    if (filters.senderCountry != null) {
      shipmentSearchFilters.push({
        field: "addressFrom.countryId",
        type: "TEXT",
        operation: "=",
        values: [filters.senderCountry.id]
      });
    }
    if (filters.receiverCountry != null) {
      shipmentSearchFilters.push({
        field: "addressTo.countryId",
        type: "TEXT",
        operation: "=",
        values: [filters.receiverCountry.id]
      });
    }
    if (filters.packageType != null) {
      shipmentSearchFilters.push({
        field: "shipPackTypeId",
        type: "INTEGER",
        operation: "=",
        values: [filters.packageType.packtypeBusinessId]
      });
    }
    if (filters.shipmentId != null) {
      shipmentSearchFilters.push({
        field: "shipmentId",
        type: "TEXT",
        operation: "=",
        values: filters.shipmentId
      });
    }
    if (filters.shipmentDirection != null && filters.shipmentDirection !== "") {
      shipmentSearchFilters.push({
        field: "shipmentDirection",
        type: "TEXT",
        operation: "=",
        values: [filters.shipmentDirection]
      });
    }
    if (filters.MBETrackingNumber && filters.MBETrackingNumber !== "") {
      orFilters.push({
        field: "mbeTracking",
        type: "TEXT",
        operation: "._.",
        values: [filters.MBETrackingNumber]
      });
      orFilters.push({
        field: "shipmentPackages.mbePackTrackingNumber",
        type: "TEXT",
        operation: "._.",
        values: [filters.MBETrackingNumber]
      });
      hasTracking = true;
    }
    if (filters.CourierTrackingNumber && filters.CourierTrackingNumber != "") {
      orFilters.push({
        field: "shipmentTrackingNumber",
        type: "TEXT",
        operation: "._.",
        values: [filters.CourierTrackingNumber]
      });
      orFilters.push({
        field: "shipmentPackages.packTrackingNumber",
        type: "TEXT",
        operation: "._.",
        values: [filters.CourierTrackingNumber]
      });
      hasTracking = true;
    }
    if (filters.reference && filters.reference != "") {
      shipmentSearchFilters.push({
        field: "refDescription",
        type: "TEXT",
        operation: "._.",
        values: [filters.reference]
      });
    }
    if (filters.delivered != null) {
      shipmentSearchFilters.push({
        field: "delivered",
        type: "BOOLEAN",
        operation: "=",
        values: [String(filters.delivered)]
      });
    }
    if (
      filters.delivered === null &&
      (tabSelected === "shipment_status_processing" ||
        tabSelected === "shipment_status_in_transit")
    ) {
      shipmentSearchFilters.push({
        field: "delivered",
        type: "BOOLEAN",
        operation: "!=",
        values: ["true"]
      });
    }
    if (filters.nameReceiver && filters?.nameReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.companyName",
        type: "TEXT",
        operation: "._.",
        values: [filters.nameReceiver]
      });
    }
    if (filters.nameSender && filters?.nameSender !== "") {
      shipmentSearchFilters.push({
        field: "addressFrom.companyName",
        type: "TEXT",
        operation: "._.",
        values: [filters.nameSender]
      });
    }
    if (filters.addressReceiver && filters.addressReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.address",
        type: "TEXT",
        operation: "._.",
        values: [filters.addressReceiver]
      });
    }
    if (filters.cityReceiver && filters.cityReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.city",
        type: "TEXT",
        operation: "._.",
        values: [filters.cityReceiver]
      });
    }
    if (filters.provinceReceiver && filters.provinceReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.province",
        type: "TEXT",
        operation: "._.",
        values: [filters.provinceReceiver]
      });
    }
    if (filters.ZipReceiver && filters.ZipReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.zipCode",
        type: "TEXT",
        operation: "._.",
        values: [filters.ZipReceiver]
      });
    }
    if (filters.contactReceiver && filters.contactReceiver !== "") {
      shipmentSearchFilters.push({
        field: "addressTo.contact",
        type: "TEXT",
        operation: "._.",
        values: [filters.contactReceiver]
      });
    }
    if (
      filters.courierManifest !== null &&
      filters.courierManifest !== undefined
    ) {
      if (filters.courierManifest === true) {
        shipmentSearchFilters.push({
          field: "manifest_created",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.courierManifest]
        });
      } else {
        shipmentSearchFilters.push({
          field: "manifest_created",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.courierManifest, null]
        });
      }
    }
    if (filters.hasAttachment !== null) {
      if (filters.hasAttachment === true) {
        shipmentSearchFilters.push({
          field: "hasAttachment",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.hasAttachment]
        });
      } else {
        shipmentSearchFilters.push({
          field: "hasAttachment",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.hasAttachment, null]
        });
      }
    }
    if (
      filters.sentPaperlessDocument !== null &&
      filters.sentPaperlessDocument !== undefined
    ) {
      if (filters.sentPaperlessDocument === true) {
        shipmentSearchFilters.push({
          field: "sentPaperlessDocument",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.sentPaperlessDocument]
        });
      } else {
        shipmentSearchFilters.push({
          field: "sentPaperlessDocument",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.sentPaperlessDocument, null]
        });
      }
    }
    if (
      filters.isEcommerceReturn !== null &&
      filters.isEcommerceReturn !== undefined
    ) {
      if (filters.isEcommerceReturn === true) {
        shipmentSearchFilters.push({
          field: "isEcommerceReturn",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.isEcommerceReturn]
        });
      } else {
        shipmentSearchFilters.push({
          field: "isEcommerceReturn",
          type: "BOOLEAN",
          operation: "=",
          values: [filters.isEcommerceReturn, null]
        });
      }
    }
    if (
      filters.extraServiceFilters &&
      filters.extraServiceFilters.some(value => value.value !== null)
    ) {
      const include = filters.extraServiceFilters.filter(
        value => value.value === "yes"
      );
      if (include?.length) {
        shipmentSearchFilters.push({
          field: "extraServices.mbeBusinessId",
          type: "INTEGER",
          operation: "=",
          values: include.map(value => value.mbeBusinessId)
        });
      }
      const exclude = filters.extraServiceFilters.filter(
        value => value.value === "no"
      );
      if (exclude?.length) {
        shipmentSearchFilters.push({
          field: "extraServices.mbeBusinessId",
          type: "INTEGER",
          operation: "!=",
          values: exclude.map(value => value.mbeBusinessId)
        });
      }
    }

    if (hasTracking) {
      //remove date when looking up with a tracking number
      const dateFilter = shipmentSearchFilters.find(
        value => value.field === "shipmentDate"
      );
      shipmentSearchFilters = shipmentSearchFilters.filter(
        value => value.field !== "shipmentDate"
      );
      //custom range dates when including tracking #771532377
      const today = new Date();
      const baseDate =
        dateFilter && dateFilter.values
          ? new Date(dateFilter.values[0])
          : today;
      let dateFrom = "";
      let dateTo = "";
      if (
        getYear(baseDate) < getYear(today) &&
        getYear(today) - getYear(baseDate) !== 1
      ) {
        //if previous year, all year
        dateFrom = format(startOfYear(baseDate), "yyyy-MM-dd");
        dateTo = format(endOfYear(baseDate), "yyyy-MM-dd");
      } else {
        //if current year "last year" to simulate current year
        dateFrom = format(addMonths(today, -11), "yyyy-MM-dd");
        dateTo = format(addMonths(today, 1), "yyyy-MM-dd");
      }
      shipmentSearchFilters.push({
        field: "shipmentDate",
        operation: ">=",
        type: "DATE",
        values: [dateFrom]
      });
      shipmentSearchFilters.push({
        field: "shipmentDate",
        operation: "<=",
        type: "DATE",
        values: [dateTo]
      });
    }
    let sortBy = undefined;
    if (search.orderField !== "") {
      sortBy = search.orderField + " " + search.orderDirection;
    }
    const filtersParamsReturn = [shipmentSearchFilters, orFilters, sortBy];
    return filtersParamsReturn;
  }

  static getShipment(shipmentId: string): Promise<ShipmentDTO> {
    return axios
      .get(store.getters.BASE_API_URL + "/shipments/online-mbe/" + shipmentId)
      .then(res => res.data);
  }

  static saveShipment(
    shipment: ShipmentDTO,
    tenantId: number,
    multiStoreId: number,
    printWaybill = false,
    new_shipment = false,
    messages?: MessageResult[]
  ): Promise<CreateOrUpdateShipmentDTO> {
    if (messages && messages.length > 0) {
      // Add messages to object to save
      for (let i = 0; i < messages.length; i++) {
        messages[i].checked = true;
      }
    }

    let body: CreateOrUpdateShipmentDTO = {
      messageResult: {
        messages: messages && messages.length > 0 ? messages : []
      },
      shipment: _.cloneDeep(shipment)
    };

    body = ShipmentService.prepareCreateOrUpdateShipmentDTO(
      body,
      tenantId,
      multiStoreId,
      printWaybill,
      new_shipment
    );
    return axios({
      method: "POST",
      timeout: 28000,
      url: store.getters.BASE_API_URL + "/shipments/online-mbe",
      params: {
        action: "SAVE"
      },
      data: body
    }).then(res => res.data);
  }

  static saveShipmentDraft(
    shipment: ShipmentDTO,
    tenantId: number,
    multiStoreId: number
  ): Promise<CreateOrUpdateShipmentDTO> {
    shipment.shipmentId =
      shipment.shipmentId === "" ? undefined : shipment.shipmentId;
    let body: any = {
      shipment: _.cloneDeep(shipment)
    };

    body = ShipmentService.prepareCreateOrUpdateShipmentDTO(
      body,
      tenantId,
      multiStoreId,
      false,
      false
    );
    return axios({
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/online-mbe",
      params: {
        action: "DRAFT"
      },
      data: body
    }).then(res => res.data);
  }

  static generateExportShipTrace(
    customerId: number,
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 20,
      orderField: "",
      orderDirection: ""
    } as SearchParams,
    filters: ShipmentSearchFilters,
    context: string,
    isWine = false
  ) {
    const filtersParam = this.filtersParams(customerId, search, filters);
    const operation = isWine ? "=" : "!=";
    const shipmentSearchFilters = [
      ...filtersParam[0],
      {
        field: "shipMBEProducts.idSpecialtyBusiness",
        type: "INTEGER",
        operation,
        values: [6, 8, 9, 10]
      }
    ];
    const orFilters = filtersParam[1];
    return axios({
      url: store.getters.BASE_API_URL + `/shipments/online-export/${context}`,
      method: "POST",
      data: {
        and: shipmentSearchFilters,
        or: orFilters.length > 0 ? orFilters : undefined
      },
      params: {
        language: store.getters.language
      }
    }).then(res => {
      return res.data;
    });
  }

  static getExportShipTrace(
    customerId: number,
    filters?: ShipmentSearchFilters
  ) {
    return axios({
      url:
        store.getters.BASE_API_URL +
        `/shipments/online-export/shipments-management-mol`,
      method: "GET",
      data: {
        customerId
      },
      params: {
        language: store.getters.language
      }
    }).then(res => {
      return res.data;
    });
  }

  static setExportShipTrace(
    customerId: number,
    templateName: string,
    presets: any[]
  ) {
    return axios({
      url:
        store.getters.BASE_API_URL +
        `/shipments/online-export/shipments-management-mol`,
      method: "POST",
      data: {
        customerId,
        templateName,
        presets
      }
    }).then(res => {
      return res.data;
    });
  }

  static deleteMetadataFor(
    microserviceName: string,
    contextId: string,
    tenantId?: number
  ): Promise<SearchMetadataDto> {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${store.getters.BASE_API_URL}/${microserviceName}/search/metadata/${contextId}`,
      params: {
        tenantId: tenantId
      }
    };
    return axios.request(config).then(res => res.data);
  }

  /**
   * Creates/updates search and filtering metadata for the indicated context ID
   */
  static createOrUpdateMetadataFor(
    microserviceName: string,
    contextId: string,
    searchMetadataDto: SearchMetadataDto
  ): Promise<SearchMetadataDto> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${store.getters.BASE_API_URL}/${microserviceName}/search/metadata/${contextId}`,
      data: searchMetadataDto
    };
    return axios.request(config).then(res => res.data);
  }

  /**
   * Query templates for current user
   */
  static queryMetadataCurrentUser(
    pageSize: number,
    page: number,
    sortBy?: string,
    context?: string
  ): Promise<Pageable<SearchMetadataDto>> {
    return axios({
      url:
        store.getters.BASE_API_URL + `/shipments/search/metadata/query-current`,
      method: "POST",
      data: {
        context: context
      },
      params: {
        sort: sortBy,
        size: pageSize,
        page: page
      },
      progress: false
    } as AxiosRequestConfigDef).then(res => {
      return res.data;
    });
  }

  static getPreSignedShipmentDocumentURL(
    documentPath: string
  ): Promise<string> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/files-storage/presigned-url`,
      params: {
        path: documentPath
      }
    };
    return axios.request(config).then(res => res.data as string);
  }

  static getExportList(sortBy?: string, pageSize?: number, page?: number) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/export`,
      params: {
        sortBy: sortBy,
        size: pageSize,
        page: page
      }
    };
    return axios.request(config).then(res => res.data);
  }

  static prepareCreateOrUpdateShipmentDTO(
    body: CreateOrUpdateShipmentDTO,
    tenantId: number,
    multiStoreId: number,
    printWaybill: boolean,
    new_shipment: boolean
  ): CreateOrUpdateShipmentDTO {
    body.shipment.shipmentId === "" ? delete body.shipment.shipmentId : null;
    body.shipment.tenantId = tenantId;
    body.shipment.multiStoreId = multiStoreId;
    delete body.shipment.storeCode;
    body.shipment.status != "FIRST_DRAFT_TO_BE_CLOSED"
      ? delete body.shipment.delivered
      : null;
    delete body.shipment.addressStore.addressId;
    delete body.shipment.addressStore.vatNumber;
    delete body.shipment.addressStore.fax;

    if (body.shipment.addressFor.addressId === 0) {
      delete body.shipment.addressFor.addressId;
    }
    delete body.shipment.addressFor.vatNumber;
    delete body.shipment.addressFor.fax;
    if (body.shipment.addressFor.macroareaName === "") {
      delete body.shipment.addressFor.macroareaName;
    }

    if (body.shipment.addressFrom.addressId === 0) {
      delete body.shipment.addressFrom.addressId;
    }
    delete body.shipment.addressFrom.vatNumber;
    delete body.shipment.addressFrom.fax;

    if (body.shipment.addressTo.addressId === 0) {
      delete body.shipment.addressTo.addressId;
    }
    delete body.shipment.addressTo.vatNumber;
    delete body.shipment.addressTo.fax;

    delete body.shipment.alternativeAddress;
    delete body.shipment.importerContactId;
    body.shipment.invoiceMbeServiceId = body.shipment.mbeServiceId;
    body.shipment.invoiceMbeServiceName = body.shipment.mbeService;
    delete body.shipment.shipmentTrackingNumber;
    delete body.shipment.excludeInvoiceCheck;
    delete body.shipment.prepaidCredit;
    delete body.shipment.mosDescription;
    delete body.shipment.courierRefund;
    delete body.shipment.customerRefund;
    delete body.shipment.courierRefundDate;
    delete body.shipment.customerRefundDate;
    delete body.shipment.originalVersion;
    delete body.shipment.lockedByUser;
    delete body.shipment.priceSource;

    // Proforma
    if (
      body.shipment.proforma !== undefined &&
      body.shipment.proforma.totalValue === 0 &&
      body.shipment.proforma.proformaDetails !== undefined &&
      body.shipment.proforma.proformaDetails.length === 0
    ) {
      delete body.shipment.proforma;
    } else {
      if (body.shipment.proforma !== undefined) {
        if (body.shipment.proforma.invoiceNumber === 0) {
          delete body.shipment.proforma.invoiceNumber;
        }

        if (body.shipment.proforma.invoiceDate === "") {
          delete body.shipment.proforma.invoiceDate;
        }

        if (body.shipment.proforma.declarationText === "") {
          delete body.shipment.proforma.declarationText;
        }

        if (body.shipment.proforma.additionalComment === "") {
          delete body.shipment.proforma.additionalComment;
        }

        if (body.shipment.proforma.eori === "") {
          delete body.shipment.proforma.eori;
        }

        if (body.shipment.proforma.incoterms === "") {
          delete body.shipment.proforma.incoterms;
        }

        if (body.shipment.proforma.vatNumber === "") {
          delete body.shipment.proforma.vatNumber;
        }

        if (body.shipment.proforma.fiscalCode === "") {
          delete body.shipment.proforma.fiscalCode;
        }
        if (body.shipment.proforma.mrnNumber === "") {
          delete body.shipment.proforma.mrnNumber;
        }
      }
    }

    delete body.shipment.courierIdentificationNumber;
    delete body.shipment.printPackageReference;
    delete body.shipment.clientIdentifier;
    delete body.shipment.documents;
    delete body.shipment.customerAccount;
    delete body.shipment.quantumViewNotification;
    delete body.shipment.source;
    delete body.shipment.status;

    printWaybill ? (body.shipment.manual = false) : delete body.shipment.manual;
    delete body.shipment.checked;
    if (
      body.shipment.accountCongifuration &&
      body.shipment.accountCongifuration.code &&
      body.shipment.accountCongifuration.code === ""
    ) {
      delete body.shipment.accountCongifuration;
    }

    delete body.shipment.agentName;
    delete body.shipment.freightCollect;
    delete body.shipment.specialtyId;
    delete body.shipment.modifiedShipNetPrice;
    delete body.shipment.mbeTracking;
    body.shipment.canPrintWayBill = printWaybill;
    delete body.shipment.currency;
    // Shipment packages
    let sequence = 1;
    body.shipment.shipmentPackages = body.shipment.shipmentPackages.map(
      (item: ShipmentPackageDTO) => {
        delete item.packTrackingNumber;
        delete item.mbePackTrackingNumber;
        item.sequence = sequence;
        if (!item.winePackType) {
          delete item.itemsNumber;
          delete item.specialItemsNumber;
        }
        delete item.sent;
        delete item.trackingLastStatusCourier;
        delete item.trackingLastStatusMbeName;
        delete item.trackingLastStatusMbeId;
        delete item.trackingDate;
        delete item.trackingSignature;
        delete item.delivered;
        delete item.seen;
        delete item.currentPosition;
        delete item.barCode;

        sequence++;

        return item;
      }
    );

    //init default shipment.costMBE to zero on save/update
    if (body.shipment.costMBE === null || body.shipment.costMBE === undefined) {
      body.shipment.costMBE = 0;
    }

    if (body.shipment.shipmentSource === "") {
      delete body.shipment.shipmentSource;
    }

    return body;
  }

  static deleteShipment(shipmentId: string): Promise<any> {
    return axios({
      method: "DELETE",
      url:
        store.getters.BASE_API_URL + "/shipments/" + shipmentId + "/online-mbe"
    });
  }

  static getAttachmentsSignedURLs(shipmentId: string): Promise<any> {
    return ShipmentService.getDocumentsSignedURLs(shipmentId, "GENERIC");
  }

  static getMBEWaybillsSignedURLs(shipmentId: string): Promise<any> {
    return ShipmentService.getDocumentsSignedURLs(
      shipmentId,
      "PACKAGE_REFERENCE"
    );
  }

  static getWaybillsSignedURLs(shipmentId: string): Promise<any> {
    return ShipmentService.getDocumentsSignedURLs(shipmentId, "WAYBILL");
  }

  getPODFiles(shipmentIds: any[]): Promise<any> {
    return axios({
      url: store.getters.BASE_API_URL + `/shipments/proof-of-delivery/pdf`,
      method: "POST",
      data: shipmentIds,
      params: {
        lang: store.getters.language,
        groupByCustomer: true
      }
    }).then(res => {
      return res.data as DocumentDTO[];
    });
  }

  getPODFilesAsync(shipmentIds: any[]): Promise<any> {
    return axios({
      url:
        store.getters.BASE_API_URL + `/shipments/proof-of-delivery/pdf/async`,
      method: "POST",
      data: shipmentIds,
      params: {
        lang: store.getters.language,
        groupByCustomer: true
      }
    }).then(res => {
      return res.data as DocumentDTO[];
    });
  }

  static getDocumentsSignedURLs(
    shipmentId: string,
    documentType: string
  ): Promise<any> {
    return axios({
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        `/shipments/${shipmentId}/presigned-document/${documentType}?convertZplPreviewWithPdfPreview=true`
    }).then(res => res.data);
  }

  static getMultipleWaybillsSignedURLs(shipmentIds: any): Promise<any> {
    return ShipmentService.getMultipleDocumentsSignedURLs(shipmentIds);
  }

  static getMultipleDocumentsSignedURLs(shipmentIds: any): Promise<any> {
    return axios({
      method: "POST",
      url: store.getters.BASE_API_URL + `/shipments/waybill/courier/combine`,
      data: shipmentIds
    }).then(res => res.data);
  }

  static uploadAttachments(shipmentId: string, files: File[]): Promise<any> {
    const data = new FormData();
    const filesArray: File[] = [];
    const documentTypes: any = {};
    filesArray.push(...files);
    filesArray.forEach(file => {
      documentTypes[file.name] = "GENERIC";
      data.append("shipmentDocuments", file);
    });
    data.append("filenameToDocumentTypeMap", JSON.stringify(documentTypes));
    return axios({
      method: "POST",
      url: store.getters.BASE_API_URL + `/shipments/${shipmentId}/documents`,
      data: data
    });
  }

  static deleteAttachments(
    shipmentId: string,
    documents: DocumentDTO[]
  ): Promise<DocumentDTO[]> {
    const data = documents.map(doc => {
      return {
        pathDocument: doc.pathDocument,
        versionId: doc.versionId,
        preview: doc.preview
      };
    });
    return axios({
      method: "DELETE",
      url: store.getters.BASE_API_URL + `/shipments/${shipmentId}/documents`,
      data: data
    }).then(res => res.data);
  }

  static closeShipments(shipments: ShipmentListItem[]): Promise<any> {
    const shipmentsIdArray: string[] = [];
    shipments.forEach(shipment => {
      shipmentsIdArray.push(shipment.shipmentId);
    });

    return ShipmentService.closeShipmentsById(shipmentsIdArray).catch(err => {
      return Promise.reject(err);
    });
  }

  static closeShipmentsById(shipmentsIdArray: string[]): Promise<any> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/online-mbe/closure",
      data: shipmentsIdArray
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const resShipments = res.data as ShipmentListItem[];
      return resShipments;
    });
  }

  static lockShipments(shipmentIds: string[]): Promise<ResultLockUnlockDto[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/lock",
      data: shipmentIds
    };

    return axios.request(config).then((res: AxiosResponse) => res.data);
  }

  static unlockShipments(
    shipmentIds: string[]
  ): Promise<ResultLockUnlockDto[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/unlock",
      data: shipmentIds
    };

    return axios.request(config).then((res: AxiosResponse) => res.data);
  }

  // Used on tab close/refresh: axios requests are cancelled on unload, fetch with keepalive is not
  static unlockShipmentsOnUnload(shipmentIds: string[]) {
    const tokenObj = retrieveToken() || retrieveKeycloakToken();
    if (!tokenObj) return;

    fetch(store.getters.BASE_API_URL + "/shipments/unlock", {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + tokenObj.access_token,
        "X-Teleport-Tenant": String(getTenantId()),
        Source: "Online",
        baseUrl: window.location.hostname
      },
      body: JSON.stringify(shipmentIds)
    }).catch(err => console.error(err));
  }

  static printManifests(
    shipments: ShipmentListItem[],
    action = "PRINT_DETAILED_MANIFEST",
    courierId = 0,
    courierName = "MBE"
  ): Promise<string[]> {
    const shipmentsIdArray: string[] = [];
    shipments.forEach(shipment => {
      shipmentsIdArray.push(shipment.shipmentId);
    });

    const manifestData = {
      shipmentIdList: shipmentsIdArray,
      action: action,
      manifestType: "ONLINE",
      courierId: courierId,
      courierName: courierId === 99 ? "Other" : courierName,
      generateNewManifest: true
    };

    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/manifest",
      data: manifestData
    };
    return axios
      .request(config)
      .then(res => {
        return res.data.base64ContentDocument;
      })
      .catch(err => {
        return Promise.reject(err);
      });
  }

  static printManifest(shipments: ShipmentListItem[]): Promise<any> {
    return ShipmentService.printManifests(shipments).then(res => res[0]);
  }

  /**
   * return base64 string of MOS document
   * @param shipmentId
   * @param language override pdf language, optional on backend
   */
  static printMOS(shipmentId: string, language: string): Promise<string> {
    return axios({
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/shipments/mos/print?shipmentId=" +
        shipmentId +
        "&pdfLanguage=" +
        language
    }).then(res => res.data);
  }

  static printMBEWaybill(
    shipmentIds: string[],
    language = "en",
    format = "fA4"
  ): Promise<any> {
    return axios({
      method: "POST",
      url:
        store.getters.BASE_API_URL +
        "/shipments/mbewaybill?pdfLanguage=" +
        language +
        "&formatType=" +
        format,
      data: shipmentIds
    }).then(res => res.data);
  }

  static printMergedWaybills(
    shipmentIds: string[],
    language = "en",
    format = "fA4",
    visiblePriceInWaybill
  ): Promise<any> {
    return axios({
      method: "POST",
      url:
        store.getters.BASE_API_URL +
        "/shipments/waybill/all/combine" +
        "?formatType=" +
        format +
        "&language=" +
        language +
        "&visiblePriceInWaybill=" +
        visiblePriceInWaybill,
      data: shipmentIds
    }).then(res => res.data);
  }

  static printMultipleMBEWaybill(
    shipmentIds: string[],
    language = "en",
    format = "fA4"
  ): Promise<any> {
    return axios({
      method: "POST",
      url:
        store.getters.BASE_API_URL +
        "/shipments/waybill/mbe/combine?pdfLanguage=" +
        language +
        "&formatType=" +
        format,
      data: shipmentIds
    }).then(res => res.data);
  }

  static getAccounts(
    courierId: number
  ): Promise<CourierServiceCodesConfigurationDto[]> {
    const config: AxiosRequestConfig = {
      url: store.getters.BASE_API_URL + "/shipments/couriers/accounts",
      method: "GET",
      params: {
        storeId: getStoreId(),
        multistoreId: getMultiStoreId(),
        courierId: courierId
      }
    };

    return axios.request(config).then(res => {
      return res.data as CourierServiceCodesConfigurationDto[];
    });
  }

  static molUserCode(username: string): Promise<any> {
    const config: AxiosRequestConfig = {
      url: store.getters.BASE_API_URL + "/shipments/account/mol-user-code",
      method: "GET",
      params: {
        username
      }
    };

    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static molUserAccount(
    idCodeConfiguration: number
  ): Promise<CourierServiceCodesConfigurationDto[]> {
    const config: AxiosRequestConfig = {
      url:
        store.getters.BASE_API_URL +
        "/shipments/couriers/accounts/configuration/" +
        idCodeConfiguration,
      method: "GET"
    };

    return axios.request(config).then(res => {
      return res.data as CourierServiceCodesConfigurationDto[];
    });
  }

  getZipRegistry(countryId: any, zipCode: any, province: any, city: any) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/zip-registry",
      params: {
        country: countryId,
        zipcode: zipCode,
        province: province,
        city: city,
        size: 2000,
        sort: "id.postcodeFrom,id.postcodeUntil,id.townName,id.province,asc"
      }
    };
    return axios.request(config).then((res: any) => {
      return res;
    });
  }

  getAccessPointZipRegistry(params: any) {
    const defaultPagination = {
      page: 0,
      size: 25,
      sort: "id.postcodeFrom,id.postcodeUntil,id.townName,id.province,asc"
    };
    const param = Object.assign(defaultPagination, params);
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/zip-registry",
      params: param
    };
    return axios.request(config).then((res: any) => {
      return res;
    });
  }

  static manageCourierPickup(action: string, req: ManagePickupRequestDTO) {
    // Remove unneeded fields (testing)
    if (
      req.pickupDto.alternativeFrom === null ||
      req.pickupDto.alternativeFrom === ""
    )
      delete req.pickupDto.alternativeFrom;
    if (
      req.pickupDto.alternativeTo === null ||
      req.pickupDto.alternativeTo === ""
    )
      delete req.pickupDto.alternativeTo;
    if (
      req.pickupDto.bookingIdentificationNumber === null ||
      req.pickupDto.bookingIdentificationNumber === ""
    )
      delete req.pickupDto.bookingIdentificationNumber;
    if (req.pickupDto.checksum === null || req.pickupDto.checksum === "")
      delete req.pickupDto.checksum;
    if (req.pickupDto.creationDate === null || req.pickupDto.creationDate === 0)
      delete req.pickupDto.creationDate;
    // req.pickupDto.deleted
    if (req.pickupDto.note === null || req.pickupDto.note === "")
      delete req.pickupDto.note;
    if (
      req.pickupDto.rejectedReason === null ||
      req.pickupDto.rejectedReason === ""
    )
      delete req.pickupDto.rejectedReason;
    if (
      req.pickupDto.requestLocation === null ||
      req.pickupDto.requestLocation === ""
    )
      delete req.pickupDto.requestLocation;
    // req.pickupDto.status
    return axios({
      method: "POST",
      url:
        store.getters.BASE_API_URL + "/shipments/managePickup?action=" + action,
      data: req
    }).then(res => res.data);
  }

  static getImportNotificationsPaged(
    storeId: string,
    username: string,
    pageSize: number,
    page: number,
    sortBy?: string
  ): Promise<Pageable<ShipImportNotificationDto>> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/shipments/notifications-paged`,
      params: {
        store_id: storeId,
        username: username,
        sort: sortBy ? sortBy : "insertDate,desc",
        size: pageSize,
        page: page
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  // /shipments/ecommerce/outcome/{id}/detail
  static getEcommerceOutcomeDetail(id: number) {
    return axios
      .get(
        store.getters.BASE_API_URL + `/shipments/ecommerce/outcome/${id}/detail`
      )
      .then(response => response.data);
  }

  // shipments/ecommerce/outcome
  static getEcommerceOutcome(
    pageSize: number,
    page: number,
    sortBy?: string,
    ecommerceType?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<Pageable<EcommerceOutcomeDto>> {
    /*
  @RequestParam(value = "ecommerceType", required = false) String ecommerceType, //AMAZON, EBAY
  @RequestParam(value = "fromDate", required = false) String fromDate, //FORMAT: yyyy-MM-dd
  @RequestParam(value = "toDate", required = false) String toDate, //FORMAT: yyyy-MM-dd

  /shipments/ecommerce/outcome
  */
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/ecommerce/outcome",
      params: {
        ecommerceType: ecommerceType,
        fromDate: fromDate,
        toDate: toDate,
        sort: sortBy ? sortBy : "insertDate,desc",
        size: pageSize,
        page: page
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  getAvailableAccessPoints(
    address: any,
    shipmentType: string,
    courierServiceId: number,
    courierId: number,
    courierName: string
  ) {
    const body = {
      address: address,
      shipmentType: shipmentType,
      courierServiceId: courierServiceId,
      courierId: courierId,
      courierName: courierName
    };
    return axios
      .post(store.getters.BASE_API_URL + "/shipments/location_service", body)
      .then(res => res.data);
  }

  static importShipment(formData: any): Promise<ApiResponse> {
    const config: AxiosRequestConfig = {
      method: "POST",
      timeout: 28000,
      url: store.getters.BASE_API_URL + "/shipments/import/online-import",
      data: formData,
      params: {
        language: store.getters.language
      },
      headers: {
        "X-Debug-Log": store.getters.envs.isDev
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static importAmazonShipments(formData: any) {
    const config: AxiosRequestConfig = {
      method: "POST",
      timeout: 28000,
      url:
        store.getters.BASE_API_URL +
        "/shipments/amazon/getShipmentsFromOrdersAsync",
      data: formData
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static refreshAmazonToken(
    state: string,
    selling_partner_id: string,
    spapi_oauth_code: string
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/amazon/refresh-token",
      data: {
        state,
        selling_partner_id,
        spapi_oauth_code
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  getPricelistKeys(keys: string, customerId: number, like?: boolean) {
    const objArray: ConfigurationDTO[] = [];
    const obj = {
      tenantId: getTenantId(),
      multistoreId: getMultiStoreId(),
      customerId: customerId,
      confKey: keys
    } as ConfigurationDTO;
    objArray.push(obj);

    const config: AxiosRequestConfig = {
      method: "POST",
      data: objArray,
      url: store.getters.BASE_API_URL + "/pricelist/kvstore/configurations",
      params: {
        "like-parameter": like
      }
    };
    if (like) {
      config.headers = {
        "like-parameter": like // temp patch backend bug
      };
    }
    return axios
      .request(config)
      .then((res: AxiosResponse<ConfigurationDTO[]>) => {
        return res.data as ConfigurationDTO[];
      });
  }
  getTaxAndDutyKeys() {
    const objArray: ConfigurationDTO[] = [];
    const obj = {
      tenantId: getTenantId(),
      multistoreId: getMultiStoreId(),
      storeId: getStoreId(),
      confKey: "TAX_AND_DUTIES_ACTIVE"
    } as ConfigurationDTO;
    objArray.push(obj);
    const obj1 = {
      tenantId: getTenantId(),
      multistoreId: getMultiStoreId(),
      storeId: getStoreId(),
      confKey: "TAX_AND_DUTIES_ZONOS_ACTIVE"
    } as ConfigurationDTO;
    objArray.push(obj1);
    const obj2 = {
      tenantId: getTenantId(),
      confKey: "SYS_ACTIVATION_PRIOR_NOTICE_POPUP"
    } as ConfigurationDTO;
    objArray.push(obj2);

    const config: AxiosRequestConfig = {
      method: "POST",
      data: objArray,
      url: store.getters.BASE_API_URL + "/stores/kvstore/configurations"
    };
    return axios
      .request(config)
      .then((res: AxiosResponse<ConfigurationDTO[]>) => {
        return res.data as ConfigurationDTO[];
      });
  }

  getStoreKeys(data: ConfigurationDTO[]) {
    const config: AxiosRequestConfig = {
      method: "POST",
      data,
      url: store.getters.BASE_API_URL + "/stores/kvstore/configurations"
    };
    return axios
      .request(config)
      .then((res: AxiosResponse<ConfigurationDTO[]>) => {
        return res.data as ConfigurationDTO[];
      });
  }

  getShipmentKeys(data: ConfigurationDTO[]) {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: data,
      url: store.getters.BASE_API_URL + "/shipments/kvstore/configurations"
    };

    return axios
      .request(config)
      .then((res: AxiosResponse<ConfigurationDTO[]>) => {
        return res.data as ConfigurationDTO[];
      });
  }

  getKeysSV4B(type: any, shipType: any, customerId: any) {
    const url = `/pricelist/configuration/sv4b?customerId=${customerId}&shipType=${shipType}&type=${type}`;
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + url
    };
    return axios
      .request(config)
      .then((res: AxiosResponse<Sv4bConfigurationDTO>) => {
        const configurationDto = res.data as Sv4bConfigurationDTO;
        return configurationDto;
      });
  }

  getCustomDuties(customerId: number, username: string, courierId: number) {
    const config = {
      url:
        store.getters.BASE_API_URL +
        `/customers/${customerId}/onlinembe/customDuties/${username}/${courierId}`,
      method: "GET"
    } as AxiosRequestConfig;
    return axios.request(config).then((res: AxiosResponse) => {
      return res.data;
    });
  }

  getPaperlessDocuments(
    // courierId?: number,
    mbeDocumentTypesId: number,
    customerId: number,
    molUsername: string,
    searchParams?: SearchParams,
    showCustomerDocuments = false,
    courier?: number
  ) {
    return axios
      .get(store.getters.BASE_API_URL + "/shipments/paperless-documents/mol", {
        params: {
          "mbe-document-types-id": mbeDocumentTypesId,
          customerId: customerId,
          molUsername: molUsername,
          showCustomerDocuments: showCustomerDocuments,
          courierId: courier,
          page: searchParams.page,
          size: searchParams.pageSize,
          sort: searchParams.orderField + "," + searchParams.orderDirection
        }
      })
      .then(res => res.data)
      .catch(err => {
        console.error(err.message);
        return Promise.reject(err);
      });
  }

  uploadPaperlessDocument(formData: any): Promise<PaperlessDocumentDto[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/paperless-documents/upload",
      data: formData
    };
    return axios
      .request(config)
      .then(res => {
        return res.data as PaperlessDocumentDto[];
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getAvailablePaperlessCourierDocumentTypesByCourierId(
    mbeDocumentTypesId: string,
    courierId?: string
  ) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/shipments/paperless-documents/courier-document-type",
        {
          params: {
            courierId: courierId,
            "mbe-document-types-id": mbeDocumentTypesId
          }
        }
      )
      .then(res => res.data)
      .catch(err => {
        console.error(err.message);
        return Promise.reject(err);
      });
  }

  getAvailablePaperlessDocumentTypes(courierId?: any) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/shipments/paperless-documents/document-type",
        {
          params: {
            courierId: courierId,
            excludeProforma: false
          }
        }
      )
      .then(res => res.data)
      .catch(err => console.error(err.message));
  }

  verifyPaperlessUsage(courierId?: number): Promise<boolean> {
    const params = courierId ? { courierId: courierId } : {};

    return axios
      .get(
        store.getters.BASE_API_URL + "/stores/verify-paperless-permissions",
        { params }
      )
      .then(response => {
        // Return true if the response data is true, indicating paperless can be used
        return response.data === true;
      })
      .catch(error => {
        console.error("Error:", error);
        // Return false in case of an error or when the response data is false, indicating paperless cannot be used
        return false;
      });
  }

  getPaperlessContentDocumentByIdAndCourierId(id: string, courierId?: string) {
    return axios
      .get(
        store.getters.BASE_API_URL + "/shipments/paperless-documents/content",
        {
          params: {
            id: id,
            courierId: courierId
          }
        }
      )
      .then(res => res.data)
      .catch(err => {
        console.error(err.message);
        return Promise.reject(err);
      });
  }

  getPaperlessContentDocumentsByIds(
    ids: number[] | undefined,
    onlyCustomerDocuments?: boolean,
    active?: boolean
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: ids,
      params: { onlyCustomerDocuments: onlyCustomerDocuments, active: active },
      url:
        store.getters.BASE_API_URL + "/shipments/paperless-documents/contents"
    };

    return axios.request(config).then(res => {
      return res.data;
    });
  }

  deletePaperlessDocumentByIds(ids: any[]) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      data: ids,
      url: store.getters.BASE_API_URL + "/shipments/paperless-documents"
    };

    return axios.request(config).then(res => {
      return res.data;
    });
  }

  getReasons() {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/proforma/reasons"
    };
    return axios
      .request(config)
      .then((res: any) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }
  getCutoffPickup(storeId: any, customerId: any) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/shipments/pickup/cutoff",
      data: {
        storeId: storeId,
        customerId: customerId
      }
    };
    return axios
      .request(config)
      .then((res: any) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getProformaDeclarationStatements() {
    const config: AxiosRequestConfig = {
      method: "GET",
      url:
        store.getters.BASE_API_URL + "/shipments/proforma/declarationStatements"
    };
    return axios
      .request(config)
      .then((res: any) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getProformaVatStatuses() {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/proforma/vatStatuses"
    };
    return axios
      .request(config)
      .then((res: any) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getInvoiceCheckConfigurations(
    pageable: Page,
    customerMbeId?: any
  ): Promise<Pageable<InvoiceCheckConfigurationDto>> {
    const config: AxiosRequestConfig = {
      url:
        store.getters.BASE_API_URL + "/shipments/invoice-check/configuration",
      method: "GET",
      params: {
        page: pageable.pageNumber,
        pageSize: pageable.pageSize,
        customerMbeId: customerMbeId
      }
    };

    return axios
      .request(config)
      .then(res => {
        return res.data as Pageable<InvoiceCheckConfigurationDto>;
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  static getImportTemplate(): Promise<any> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/import/online-template",
      responseType: "arraybuffer",
      params: {
        language: store.getters.language
      }
    };
    return axios.request(config).then(res => {
      return res;
    });
  }

  static getSpecialty(shipment: ShipmentDTO, specialtyBusiness: number) {
    return (
      shipment.shipMBEProducts &&
      shipment.shipMBEProducts.find(
        value => specialtyBusiness === value.idSpecialtyBusiness
      )
    );
  }

  static duplicateShipment(
    value: ShipmentDTO,
    emptyCourierWeightsOnDuplicate: boolean
  ): ShipmentDTO {
    const today = new Date();
    if (value.shipMBEProducts) {
      value.shipMBEProducts.forEach((sp, i) => {
        if (value.shipMBEProducts[i] && value.shipMBEProducts[i].idWebService) {
          value.shipMBEProducts[i].idWebService = 0;
        }
      });
    }
    const shipment = {
      ...value,
      active: true, // #526181199 forcing the duplicated shipment to active
      shipmentPackages: value.shipmentPackages.map(
        (value: ShipmentPackageDTO) => {
          //keep only the fields we need!!
          return this.duplicatePackage(value, emptyCourierWeightsOnDuplicate);
        }
      ),
      extraServices: value.extraServices,
      documents: undefined,
      invoiceDate: undefined,
      invoiceFormattedSaleNumber: undefined,
      invoiceId: undefined,
      shipmentId: "",
      id: null,
      mbeTracking: null,
      shipmentTrackingNumber: undefined,
      status: "DRAFT_TO_BE_CLOSED",
      shipmentDate: format(today, "yyyy-MM-dd"),
      //#4032 reset pickup data
      pickup: undefined,
      pickupRequest: false,
      //#2848 remove original source when duplicating, set default
      source: "SYSTEM",
      sourceTypeBusiness: "SINGLE_SHIPMENT",
      //564085919 do not duplicate the new sources
      shipmentSource: undefined,
      shipmentSourceTypeBusiness: undefined,
      // update new review see #642531033
      reportLastUpdate: undefined,
      mbelabErrorReason: undefined,
      delivered: false,
      emailSent: false,
      excludeInvoiceCheck: false,
      pickupRequested: false,
      exception: false,
      sentToLabStatus: undefined,
      lastUpdateDate: undefined,
      sentEcommerceFeedbackInformation: undefined,
      trackingLink: undefined,
      deliveredDate: undefined,
      priceSource: undefined,
      toBeSent: false,
      sendDate: undefined,
      idCISShipment: undefined,
      idCISWine: undefined,
      deletionReason: undefined,
      importedShipment: false,
      deliveryDocument: undefined,
      saveShipmentTemplate: false,
      podMailSent: false,
      otherCourierName: undefined
    } as ShipmentDTO;

    if (!shipment.proforma) {
      shipment.proforma = {
        currency: "EUR",
        totalValue: 0,
        proformaDetails: []
      } as ProformaDTO;
    }

    // reset some proforma data see #642531033
    if (shipment.proforma.invoiceDate) {
      shipment.proforma.invoiceDate = format(today, "yyyy-MM-dd");
    }
    if (shipment.proforma.invoiceNumber) {
      shipment.proforma.invoiceNumber = Number(format(today, "yyyyMMddHHmm"));
    }
    if (shipment.proforma.proformaDetails) {
      shipment.proforma.proformaDetails = shipment.proforma.proformaDetails.map(
        (item: ProformaDetailsDTO) => {
          return {
            ...item,
            type: item.descriptiveRow === false ? 1 : 2
          };
        }
      );
    }

    return shipment;
  }

  static duplicatePackage(
    value: ShipmentPackageDTO,
    emptyCourierWeightsOnDuplicate: boolean
  ): ShipmentPackageDTO {
    return {
      sequence: value.sequence,
      courierWeight: emptyCourierWeightsOnDuplicate
        ? value.customerWeight
        : value.courierWeight,
      courierVolumetricWeight: emptyCourierWeightsOnDuplicate
        ? 0
        : value.courierVolumetricWeight,
      courierTaxedWeight: emptyCourierWeightsOnDuplicate
        ? 0
        : value.courierTaxedWeight,
      customerWeight: value.customerWeight,
      customerVolumetricWeight: value.customerVolumetricWeight,
      customerTaxedWeight: value.customerTaxedWeight,
      depth: value.depth,
      width: value.width,
      height: value.height,
      itemsNumber: value.itemsNumber,
      addHandling: value.addHandling,
      oversize: value.oversize,
      overweight: value.overweight,
      packTrackingNumber: "",
      mbePackTrackingNumber: ""
    } as ShipmentPackageDTO;
  }

  static shipmentFromCalculate(
    row: any,
    ship: any,
    shipCost: any,
    inbound: boolean
  ): ShipmentDTO {
    row.mbeServiceId = JSON.parse(JSON.stringify(row.mbeServiceIdBusiness));
    row.mbeServiceIdBusiness = undefined;
    const today = new Date();
    const shipment = {
      ...row,
      active: true, // #526181199 forcing the duplicated shipment to active
      shipmentPackages: ship.packages.map((value: ShipmentPackageDTO) => {
        //keep only the fields we need!!
        return this.duplicatePackage(value, true);
      }),
      addressTo: inbound
        ? undefined
        : {
            city: shipCost.addressTo.city,
            zipCode: shipCost.addressTo.zipCode,
            countryId: shipCost.addressTo.country.id,
            countryName: shipCost.addressTo.country.name,
            macroareaName: shipCost.addressTo.subcountry.macroAreaName,
            subcountry: shipCost.addressTo.subcountry.description,
            subcountryId: shipCost.addressTo.subcountry.idSubcountry,
            province: shipCost.addressTo.province
          },
      addressFrom: !inbound
        ? undefined
        : {
            city: shipCost.addressFrom.city,
            zipCode: shipCost.addressFrom.zipCode,
            countryId: shipCost.addressFrom.country.id,
            countryName: shipCost.addressFrom.country.name,
            macroareaName: shipCost.addressFrom.subcountry.macroAreaName,
            subcountry: shipCost.addressFrom.subcountry.description,
            subcountryId: shipCost.addressFrom.subcountry.idSubcountry,
            province: shipCost.addressFrom.province
          },
      customerWeight: row.taxedCustomerWeight,
      mbeService: row.mbeServiceName,
      mbeServiceIdBussiness: undefined,
      mbeServiceId: row.mbeServiceId,
      shipPackType: row.mbePackTypeName,
      extraServices: row.surcharges,
      documents: undefined,
      invoiceDate: undefined,
      invoiceFormattedSaleNumber: undefined,
      invoiceId: undefined,
      shipmentId: "",
      id: null,
      mbeTracking: null,
      shipmentTrackingNumber: undefined,
      status: "DRAFT_TO_BE_CLOSED",
      shipmentDate: format(today, "yyyy-MM-dd"),
      //#4032 reset pickup data
      pickup: undefined,
      pickupRequest: false,
      //#2848 remove original source when duplicating, set default
      source: "SYSTEM",
      sourceTypeBusiness: "SINGLE_SHIPMENT",
      //564085919 do not duplicate the new sources
      shipmentSource: undefined,
      shipmentSourceTypeBusiness: undefined,
      // update new review see #642531033
      reportLastUpdate: undefined,
      mbelabErrorReason: undefined,
      delivered: false,
      emailSent: false,
      excludeInvoiceCheck: false,
      pickupRequested: false,
      exception: false,
      sentToLabStatus: undefined,
      lastUpdateDate: undefined,
      sentEcommerceFeedbackInformation: undefined,
      trackingLink: undefined,
      deliveredDate: undefined,
      priceSource: undefined,
      toBeSent: false,
      sendDate: undefined,
      idCISShipment: undefined,
      idCISWine: undefined,
      deletionReason: undefined,
      importedShipment: false,
      deliveryDocument: undefined,
      saveShipmentTemplate: false,
      podMailSent: false,
      otherCourierName: undefined
    } as ShipmentDTO;

    if (!shipment.proforma) {
      shipment.proforma = {
        currency: (this as any).mbeCurrencyISO,
        totalValue: 0
      } as ProformaDTO;
    }
    // reset some proforma data see #642531033
    if (shipment.proforma.invoiceDate) {
      shipment.proforma.invoiceDate = format(today, "yyyy-MM-dd");
    }
    if (shipment.proforma.invoiceNumber) {
      shipment.proforma.invoiceNumber = Number(format(today, "yyyyMMddHHmm"));
    }
    return shipment;
  }

  /**
   * Get search and filtering metadata for the indicated context ID
   */
  static getMetadata(
    microserviceName: string,
    contextId: string
  ): Promise<SearchMetadataDto> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/${microserviceName}/search/metadata/${contextId}`
    };
    return axios.request(config).then(res => res.data);
  }

  /**
   * Creates/updates search and filtering metadata for the indicated context ID
   */
  static getAutocompleteData(url: string, value: string): Promise<any> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}${url}`,
      params: {
        search: value
      }
    };
    return axios.request(config).then(res => res.data);
  }

  static getShipmentColumns(userId: number): Promise<ColumnDTO[]> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/shipments/shipment-management/configuration/user/" +
          userId
      )
      .then(res => res.data)
      .catch(err => console.error(err.message));
  }

  getSpecificPackTypes(courierId: number) {
    return axios
      .get(store.getters.BASE_API_URL + "/shipments/specificpacktype", {
        params: {
          courierId: courierId
        }
      })
      .then(res => res.data)
      .catch(err => console.error(err.message));
  }

  getHandlingInstructions() {
    const config = {
      url: store.getters.BASE_API_URL + "/shipments/handling-instructions",
      method: "GET"
    };

    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static getPickupCouriers(): Promise<any[]> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/onlinembe/couriervisibilityconfig/pickupCouriers"
      )
      .then(res => res.data)
      .catch(err => console.error(err.message));
  }
  createShipmentTemplate(template: any, customerId: string, username: string) {
    const config = {
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe/shipmentTemplates/" +
        username,
      method: "POST",
      data: template
    } as AxiosRequestConfig;

    return axios.request(config).then(res => {
      return res.data;
    });
  }
  deleteShipmentTemplate(template: any, customerId: string, username: string) {
    const config = {
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe/shipmentTemplates",
      method: "DELETE",
      data: template
    } as AxiosRequestConfig;

    return axios.request(config).then(res => {
      return res.data;
    });
  }
  searchShipmentTemplates(
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 25
    } as SearchParams,
    customerId: string,
    username: string
  ) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe/shipmentTemplates/" +
        username,
      params: {
        page: search.page,
        size: search.pageSize,
        sort: "name",
        name: search.search != "" ? search.search : null
      }
    };
    return axios.request(config).then((res: AxiosResponse) => {
      const templates = res.data.content;
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.number,
        pageSize: res.data.size
      } as SearchParams;

      return { templates, searchParams };
    });
  }
  updateShipmentTemplate(template: any, customerId: string, username: string) {
    const config = {
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe/shipmentTemplates/" +
        username,
      method: "PUT",
      data: template
    } as AxiosRequestConfig;

    return axios.request(config).then(res => {
      return res.data;
    });
  }

  parseDistanceResult(distanceResult: any): DistanceResultDef {
    //see google.maps.DistanceMatrixResult for typing
    //calculate
    const resultDistance = {
      humanReadable: "0.0 km",
      inMeters: 0,
      hasResult: false
    } as DistanceResultDef;

    if (distanceResult) {
      if (distanceResult.rows.length > 0) {
        if (distanceResult.rows[0].elements.length > 0) {
          resultDistance.humanReadable =
            distanceResult.rows[0].elements[0].distance.humanReadable;
          resultDistance.inMeters =
            distanceResult.rows[0].elements[0].distance.inMeters;
          resultDistance.hasResult = true;
        }
      }
    }
    return resultDistance;
  }

  distanceMatrix(
    origin: AddressShipmentDTO,
    destination: AddressShipmentDTO
  ): Promise<any> {
    const config = {
      url: store.getters.BASE_API_URL + `/shipments/gmaps/distancematrix/`,
      method: "POST",
      data: {
        origin: origin,
        destination: destination,
        language: store.getters.language
      }
    } as AxiosRequestConfig;

    return axios
      .request(config)
      .then(res => {
        return res.data as any;
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  static getShipmentRates(data): Promise<ShipmentRatesResultDTO[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${store.getters.BASE_API_URL}/shipments/rates`,
      data: data,
      headers: {
        "x-debug-log": "true"
      }
    };
    return axios.request(config).then(res => res.data);
  }

  getDefaultWineImporter(storeId: number | null = null) {
    const config: AxiosRequestConfig = {
      method: "GET",
      params: {
        storeId
      },
      url:
        store.getters.BASE_API_URL + "/shipments/wine2/wine-default-warehouse"
    };
    return axios
      .request(config)
      .then((res: any) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getWine2ImporterById(id: number) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/shipments/wine2/wine-warehouse/" + id
    };

    return axios
      .request(config)
      .then(res => res.data as WineWarehouseImporterDTO);
  }
}
